import { useTranslation } from '@hooks/useTranslation';
import { ProcessStatus } from '@models/process.model';
import { UserRoles } from '@models/user.model';
import { MoreVertOutlined } from '@mui/icons-material';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { BASE_DOCS_LINK_URL } from '@utils/constants';
import { useState } from 'react';

interface MenuOption {
  text: string;
  action: () => void;
  icon?: React.ReactNode;
  color?: 'primary' | 'error';
  disabled?: boolean;
}

interface ProcessHeaderDropdownProps {
  location: string;
  processStatus: ProcessStatus;
  onDelete: () => void;
  onEdit: () => void;
  handleShowJsonImport: () => void;
  handleShowJsonExport: () => void;
  onStatusChange: (newStatus: ProcessStatus) => void;
  handleCloneProcess: () => void;
  handleShare: () => void;
  handleMarkExecution: () => void;
  handlePrintPreview?: () => void;
  handleViewVersionHistory?: () => void;
  hasVersions?: boolean;
  isExecuting?: boolean;
}

const ProcessHeaderDropdown: React.FC<ProcessHeaderDropdownProps> = ({
  location,
  processStatus,
  onDelete,
  onEdit,
  handleShowJsonImport,
  handleShowJsonExport,
  onStatusChange,
  handleCloneProcess,
  handleShare,
  handleMarkExecution,
  handlePrintPreview,
  handleViewVersionHistory,
  hasVersions = false,
  isExecuting = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const localeCommon = useTranslation('common');
  const localeProcesses = useTranslation('processes');

  const { role } = useAppSelector((state: RootState) => state.user);

  const getStatusOptions = (): MenuOption[] => {
    switch (processStatus) {
      case ProcessStatus.DRAFT:
        return [
          {
            text: localeProcesses['markAsApproved'],
            action: () => onStatusChange(ProcessStatus.DONE),
            disabled: isExecuting,
          },
        ];
      case ProcessStatus.DONE:
        return [
          {
            text: localeProcesses['markExecution'],
            action: handleMarkExecution,
          },
          {
            text: localeProcesses['markAsDraft'],
            action: () => onStatusChange(ProcessStatus.DRAFT),
            disabled: isExecuting,
          },
        ];
      default:
        return [];
    }
  };

  const options: MenuOption[] = [
    { text: localeCommon['share'], action: handleShare },
    { text: localeCommon['edit'] + ' ' + localeCommon['process'], action: onEdit, disabled: isExecuting },
    { text: localeCommon['import'], action: handleShowJsonImport, disabled: isExecuting },
    { text: localeCommon['export'], action: handleShowJsonExport },
    ...(handlePrintPreview ? [{ text: localeProcesses['printPreview'], action: handlePrintPreview }] : []),
    ...(hasVersions && handleViewVersionHistory ? [{ text: localeProcesses['versionHistory'], action: handleViewVersionHistory }] : []),
    //{ text: localeCommon['help'], action: () => {} },
  ];

  if (location) {
    options.push({ text: localeCommon['showInDocs'], action: () => navigateToDoc() });
  }

  if (role !== UserRoles.USER) {
    options.push({ text: localeCommon['clone'] + ' ' + localeCommon['process'], action: handleCloneProcess, disabled: isExecuting });
    options.push(...getStatusOptions());
    options.push({
      text: localeCommon['delete'] + ' ' + localeCommon['process'],
      action: onDelete,
      color: 'error',
      disabled: isExecuting,
    });
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigateToDoc = () => {
    window.open(BASE_DOCS_LINK_URL + location + '/edit', '_blank');
  };

  const handleMenuClick = (action: Function) => {
    action();
    handleClose();
  };

  return (
    <>
      <Box className="flex items-center">
        <IconButton onClick={handleClick}>
          <MoreVertOutlined />
        </IconButton>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          {options.map((option) => (
            <MenuItem
              key={option.text}
              onClick={() => {
                handleMenuClick(option.action);
              }}
              disabled={option.disabled}
              sx={{ color: (theme) => (option.color ? theme.palette[option.color].main : 'inherit') }}>
              {option.text}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </>
  );
};

export default ProcessHeaderDropdown;
